import { BooleanInput } from '@angular/cdk/coercion';
import {
  Component,
  DestroyRef,
  OnInit,
  ViewEncapsulation,
  booleanAttribute,
  effect,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { FormRowDirective } from '../../directives/form-row.directive';
import { Button } from '../button/button.component';
import { ExpansionPanel } from '../expansion-panel/expansion-panel.component';
import { Hint } from '../hint/hint.component';
import { InputCheck } from '../input-check/input-check.component';
import { InputText } from '../input-text/input-text.component';
import { InputTextArea } from '../input-textarea/input-textarea.component';

@Component({
  selector: 'procure-input-submittals',
  templateUrl: './input-submittals.component.html',
  styleUrl: './input-submittals.component.scss',
  imports: [
    ExpansionPanel,
    Hint,
    InputCheck,
    InputTextArea,
    InputText,
    Button,
    MatTooltip,
    FormRowDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class InputSubmittals implements OnInit {
  readonly id = input<string | null>(null);
  readonly control = input.required<FormArray>();
  readonly label = input<string>('');
  readonly hint = input<string>('');
  readonly additionalLabel = input<string>('Comments');
  readonly limitIndices = input<number[] | null>(null);

  readonly showMode = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly canAdd = input<boolean, BooleanInput>(true, {
    transform: booleanAttribute,
  });

  readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly tooltips = input<Record<number, string>>({});

  protected readonly customControl = new FormControl('', { nonNullable: true });

  protected get nameControls(): FormControl[] {
    return this.control().controls.map(
      (control) => control.get('name') as FormControl,
    );
  }

  protected get isCheckedControls(): FormControl[] {
    return this.control().controls.map(
      (control) => control.get('isChecked') as FormControl,
    );
  }

  protected get commentControls(): FormControl[] {
    return this.control().controls.map(
      (control) => control.get('comment') as FormControl,
    );
  }

  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      this.showMode();
      this.disabled();
      this.limitIndices();

      this.toggleFormInteraction();
    });
  }

  ngOnInit(): void {
    this.toggleFormInteraction();
    this.toggleLimitations();

    this.control()
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.toggleLimitations());
  }

  /**
   * Adds a new custom checkbox to the list.
   */
  addItem(): void {
    const { value } = this.customControl;

    this.control().push(
      this.formBuilder.group({
        id: 0,
        defaultRequiredSubmittalId: null,
        name: value,
        isChecked: true,
        comment: '',
        order:
          (this.control().value[this.control().value.length - 1]?.order ?? -1) +
          1,
      }),
    );

    this.customControl.reset();
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.addItem();
    }
  }

  private toggleFormInteraction(): void {
    if (this.showMode() || this.disabled()) {
      this.customControl.disable({ emitEvent: false });
    } else {
      this.customControl.enable({ emitEvent: false });
    }
  }

  private toggleLimitations(): void {
    this.isCheckedControls.forEach((control, index) => {
      const limitIndices = this.limitIndices();

      if (!limitIndices?.length) {
        control.enable({ emitEvent: false });
      } else if (!limitIndices.includes(index)) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }
}
